import { Dialog, DialogActions } from "#components/Dialog"
import { Badge } from "#components/ui/badge"
import { Button } from "#components/ui/button"
import { Label } from "#components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "#components/ui/table"
import { useAccountMembers, useRemoveMember, useUpdateMemberRole } from "#hooks/use-account-members"
import { useI18n } from "#hooks/use-i18n"
import { fadeIn } from "#lib/animations"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import { motion } from "framer-motion"
import { Edit, Trash2 } from "lucide-react"
import { useState } from "react"

interface AccountMembersListProps {
  accountId: number
  readOnly?: boolean
}

export function AccountMembersList({ accountId, readOnly = false }: AccountMembersListProps) {
  const { t } = useI18n()
  const { data: members = [], isLoading } = useAccountMembers(accountId)
  const removeMember = useRemoveMember()
  const updateMemberRole = useUpdateMemberRole()
  const [editingMember, setEditingMember] = useState<{
    userId: number
    currentRole: string
  } | null>(null)
  const [removingMember, setRemovingMember] = useState<number | null>(null)
  const [newRole, setNewRole] = useState<string>("")

  const handleRemove = async (userId: number) => {
    if (confirm(t("components.settings.members.removeMemberDescription"))) {
      try {
        await removeMember.mutateAsync({ accountId, userId })
        setRemovingMember(null)
      } catch (error) {
        alert(getUserFriendlyErrorMessage(error))
      }
    }
  }

  const handleUpdateRole = async () => {
    if (!editingMember) return

    try {
      await updateMemberRole.mutateAsync({
        accountId,
        userId: editingMember.userId,
        role: newRole,
      })
      setEditingMember(null)
      setNewRole("")
    } catch (error) {
      alert(getUserFriendlyErrorMessage(error))
    }
  }

  if (isLoading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
        Loading members...
      </motion.div>
    )
  }

  if (members.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-4 text-muted-foreground"
      >
        No members found
      </motion.div>
    )
  }

  return (
    <>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member, index) => (
              <TableRow
                key={member.id}
                className="animate-in fade-in slide-in-from-bottom-2"
                style={{
                  animationDelay: `${index * 50}ms`,
                }}
              >
                <TableCell>{member.fullName || "N/A"}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant={member.owner ? "default" : "secondary"}>{member.role}</Badge>
                    {member.owner && <Badge variant="outline">Owner</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  {!readOnly && (
                    <div className="flex items-center gap-2">
                      {!member.owner && (
                        <>
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingMember({
                                  userId: member.id,
                                  currentRole: member.role,
                                })
                                setNewRole(member.role)
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </motion.div>
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRemovingMember(member.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </motion.div>
                        </>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </motion.div>

      {/* Edit Role Dialog */}
      <Dialog
        open={!!editingMember}
        onOpenChange={(open) => !open && setEditingMember(null)}
        title={t("components.settings.members.updateRole")}
        description={t("components.settings.members.updateRoleDescription")}
        footer={
          <DialogActions
            onCancel={() => setEditingMember(null)}
            onConfirm={handleUpdateRole}
            confirmText={t("components.settings.members.updateRole")}
            cancelText={t("components.dialog.cancel")}
            isLoading={updateMemberRole.isPending}
          />
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role">{t("components.settings.members.role")}</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue placeholder={t("components.settings.members.selectRole")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{t("components.account.member")}</SelectItem>
                <SelectItem value="admin">{t("components.account.admin")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog
        open={removingMember !== null}
        onOpenChange={(open) => !open && setRemovingMember(null)}
        title={t("components.settings.members.removeMember")}
        description={t("components.settings.members.removeMemberDescription")}
        footer={
          <DialogActions
            onCancel={() => setRemovingMember(null)}
            onConfirm={() => removingMember && handleRemove(removingMember)}
            confirmText={t("components.settings.members.removeMember")}
            cancelText={t("components.dialog.cancel")}
            confirmVariant="destructive"
            isLoading={removeMember.isPending}
          />
        }
      >
        <div></div>
      </Dialog>
    </>
  )
}
